import { Item } from './Item';
import { BlindMapQuestion } from './BlindMapQuestion';
import { Resource } from './Resource';
import { isRestrictable } from './restrictionTypeguards';
import { Question } from './Question';
import { Guid } from '../types/identifiers/guid';
import { StartingMessage } from './StartingMessage';
import { HotspotTab } from './HotspotTab';

export enum ItemType {
  MenuItem = 'menuItem',
  SequencedFlow = 'sequencedFlow',
  NonSequencedFlow = 'nonSequencedFlow',
  Quiz = 'quiz',
  ResourcesFolder = 'resourcesFolder',

  // Content items
  Question = 'note',
  Split = 'split',
  Section = 'section',
  StartingMessage = 'startingMessage',
  Hotspot = 'hotspot',

  BlindMapQuestion = 'blindMapQuestion',
  SequencedFlowStep = 'sequencedFlowStep',
  Resource = 'resource',

  HotspotTab = 'hotspotTab',
  Answer = 'answer',
}

/** Declares a contract for items in course that support restricting student's navigation in a course. */
export type NavigationRestrictable = {
  restriction: NavigationRestriction | NavigationRestrictionWithAutoProgress;
};

export type ContentItemType = Question | StartingMessage | HotspotTab | BlindMapQuestion | Resource;

export type AudioAsset = {
  track: AudioTrack;
  subtitles: AudioSubtitles | null;
};

/** Text-to-speech service a voiceover is generated with; voice ids are only meaningful within it. */
export type TtsProvider = 'Azure' | 'ElevenLabs';

export type VoiceOver = {
  text: string;
  language: string;
  voiceId: string;
  provider: TtsProvider;
};

export type AudioTrack = {
  assetGuid: Guid;
  voiceOverData?: VoiceOver;
};

export type AudioSubtitles = {
  assetGuid: Guid;
};

export enum RestrictionType {
  none = 'none',
  customTime = 'customTime',
  audioDuration = 'audioDuration',
}

/** Defines a restriction for a student to move further in course until the custom duration elapses. */
export type CustomTimeNavigationRestriction = {
  restrictionType: RestrictionType.customTime;
  duration: number;
};

/** Defines a restriction for a student to move further in course until the audio assigned to the content item finishes. */
export type AudioDurationNavigationRestriction = {
  restrictionType: RestrictionType.audioDuration;
  duration?: never;
};

/** Defines a 'restriction' that is always lifted thus not blocking a student in navigating through course. */
export type NoNavigationRestriction = {
  restrictionType: RestrictionType.none;
  duration?: never;
};

/** Restriction type for which student's manual action is required before progressing in course. */
export type NavigationRestriction =
  | CustomTimeNavigationRestriction
  | AudioDurationNavigationRestriction
  | NoNavigationRestriction;

/** Indicates whether student should be autmatically navigated further in the course after restrictions are lifted. */
export type NavigationRestrictionWithAutoProgress =
  | ((CustomTimeNavigationRestriction | AudioDurationNavigationRestriction) & {
      autoProgress: boolean;
    })
  | (NoNavigationRestriction & { autoProgress?: never });

export const DefaultRestriction: NoNavigationRestriction = {
  restrictionType: RestrictionType.none,
};

export function createCustomTimeRestriction(duration: number): CustomTimeNavigationRestriction;
export function createCustomTimeRestriction(
  duration: number,
  autoProgress: boolean
): NavigationRestrictionWithAutoProgress;
export function createCustomTimeRestriction(
  duration: number,
  autoProgress?: boolean
): NavigationRestrictionWithAutoProgress | CustomTimeNavigationRestriction;
export function createCustomTimeRestriction(
  duration: number,
  autoProgress?: boolean
): NavigationRestrictionWithAutoProgress | CustomTimeNavigationRestriction {
  return {
    restrictionType: RestrictionType.customTime,
    duration,
    autoProgress: autoProgress ?? false,
  };
}

export function createAudioRestriction(): NavigationRestriction;
export function createAudioRestriction(autoProgress: boolean): NavigationRestrictionWithAutoProgress;
export function createAudioRestriction(
  autoProgress?: boolean
): NavigationRestriction | NavigationRestrictionWithAutoProgress;
export function createAudioRestriction(
  autoProgress?: boolean
): NavigationRestriction | NavigationRestrictionWithAutoProgress {
  return {
    restrictionType: RestrictionType.audioDuration,
    autoProgress: autoProgress ?? false,
  };
}

export const setAutoProgress = (
  restriction: NavigationRestrictionWithAutoProgress,
  autoProgress: boolean
): NavigationRestrictionWithAutoProgress => {
  return {
    ...restriction,
    autoProgress: autoProgress,
  } as NavigationRestrictionWithAutoProgress;
};

/** Returns true if given item has a precondition that has to be lifted before student can navigate further in a course. */
export const hasNavigationRestriction = (item: NavigationRestrictable): boolean =>
  item.restriction.restrictionType !== RestrictionType.none;

export function hasAudioRestriction(item: Item): boolean {
  return isRestrictable(item) && item.restriction.restrictionType === RestrictionType.audioDuration;
}

export enum ModalSize {
  Fullscreen = 'Fullscreen',
  Small = 'Small',
  Sidebar = 'Sidebar',
}
